import {
  BellOffOutlined,
  BellOutlined,
  FilterOutlined,
  LogoutOutlined,
  MenuOutlined,
  MobileOutlined,
  MoonOutlined,
  ReloadOutlined,
  SunOutlined,
  UserOutlined,
} from '@/app/icons';
import {
  App,
  Avatar,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
  theme,
  type MenuProps,
} from 'antd';
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  APP_NAME,
  DAILY,
  HOME,
  NAVIGATION,
  findNavigationItem,
  hueForPath,
  hueOfItem,
  isVisible,
  pageTitle,
} from '@/app/navigation';
import { useThemeStore } from '@/app/theme';
import { BrandMark } from '@/components/BrandMark';
import { Caption } from '@/components/Caption';
import { IconTile } from '@/components/IconTile';
import { useAccents } from '@/hooks/use-accents';
import { ViewFilterBar } from '@/components/ViewFilterBar';
import { isFiltering } from '@/domain/view-filter';
import { countUnfinishedToday } from '@/domain/worklog';
import { MusicDock } from '@/features/music/MusicDock';
import { VideoUploadDock } from '@/features/video-publish/VideoUploadDock';
import { DueTaskNotifier } from '@/features/worklog/DueTaskNotifier';
import { useCompactLayout, usePhoneLayout } from '@/hooks/use-media-query';
import { useNow } from '@/hooks/use-now';
import { useOwnerFiltered, useWorklog } from '@/hooks/use-records';
import { useWorkspaceRefresh } from '@/hooks/use-workspace-refresh';
import { InstallApp } from '@/components/InstallApp';
import { PushNotifications, useEnablePush } from '@/components/PushNotifications';
import { pushMenuAction } from '@/domain/push';
import { usePushStore } from '@/stores/push-store';
import { accountMenuAction } from '@/domain/pwa';
import { usePwaStore } from '@/stores/pwa-store';
import { useScreenTracking } from '@/hooks/use-screen-tracking';
import { useCurrentUser, useSessionStore } from '@/stores/session-store';
import { useViewFilterStore } from '@/stores/view-filter-store';
import { ROLES, isRoleCode } from '@halo/shared';

const { Sider, Header, Content } = Layout;
const OPEN_GROUPS_KEY = 'halo-nav-open';
/** Chiều cao thanh trên cùng, cho phần tử dính bên dưới nó: `top: var(--halo-header-height)`. */
const HEADER_HEIGHT_VAR = '--halo-header-height';

function readOpenGroups(): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? 'null');
    if (Array.isArray(stored))
      return stored.filter((key): key is string => typeof key === 'string');
  } catch {
    /* dữ liệu cũ hỏng → dùng mặc định */
  }
  return NAVIGATION.map((group) => group.key);
}

function Brand() {
  return (
    <Flex align="center" gap={10}>
      <BrandMark />
      <div style={{ lineHeight: 1.2 }}>
        <Typography.Text strong>DỰ ÁN SUN MUSIC</Typography.Text>
        <br />
        <Caption>SEO &amp; Sản xuất</Caption>
      </div>
    </Flex>
  );
}

/**
 * Khung ứng dụng, ba dạng theo bề rộng (mốc ở `hooks/use-media-query.ts`):
 *  - ≥ 1200px: thanh bên đầy đủ, bộ lọc xem nằm ngay trên thanh trên cùng;
 *  - 768–1199px: thanh bên tự thu gọn thành dải icon (< 992px), bộ lọc xem thu vào một nút;
 *  - < 768px (điện thoại): KHÔNG có thanh bên — menu nằm trong ngăn kéo mở bằng nút ☰, thanh trên
 *    cùng chỉ một hàng. Dải icon 80px trên màn 360px là mất gần một phần tư bề ngang.
 */
export function AppLayout() {
  const user = useCurrentUser();
  const logout = useSessionStore((state) => state.logout);
  const { mode, toggle } = useThemeStore();
  const { token } = theme.useToken();
  const accent = useAccents();
  const location = useLocation();
  const navigate = useNavigate();
  const isPhone = usePhoneLayout();
  const isCompact = useCompactLayout();
  const filtering = useViewFilterStore((state) => isFiltering(state.filter));
  const pwaAction = usePwaStore((state) => accountMenuAction(state.mode));
  const requestInstall = usePwaStore((state) => state.requestInstall);
  const pushAction = usePushStore((state) => pushMenuAction(state.state));
  const disablePush = usePushStore((state) => state.disable);
  const enablePush = useEnablePush();
  const { message } = App.useApp();
  // Ghi nhận mục vừa mở cho «Lịch sử truy cập» (chỉ khi đã đăng nhập).
  useScreenTracking(Boolean(user.username));
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Trạng thái gập/mở nhóm là tuỳ chọn hiển thị của từng máy → localStorage.
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  const header = useRef<HTMLElement>(null);

  useWorkspaceRefresh();

  // Huy hiệu menu: số việc hôm nay chưa xong (trong phần đang chọn xem).
  const now = useNow();
  const worklog = useOwnerFiltered(useWorklog());
  const unfinishedToday = useMemo(() => countUnfinishedToday(worklog, now), [worklog, now]);

  const menuItems = useMemo<MenuProps['items']>(() => {
    const badges: Record<string, number> = { '/kpi': unfinishedToday };
    // Icon của nhóm mang màu mực của tông; icon của từng mục nằm trong ô nền nhạt cùng tông (IconTile).
    const groups = NAVIGATION.map((group) => ({
      key: group.key,
      icon: <span style={{ color: accent[group.hue].ink }}>{group.icon}</span>,
      label: group.label,
      children: group.items
        .filter((item) => isVisible(item, user.permissions))
        .map((item) => ({
          key: item.path,
          icon: <IconTile icon={item.icon} hue={group.hue} size={24} />,
          label: (
            <Link to={item.path}>
              {item.label}
              {badges[item.path] ? (
                <Badge count={badges[item.path]} size="small" style={{ marginInlineStart: 8 }} />
              ) : null}
            </Link>
          ),
        })),
    })).filter((group) => group.children.length > 0);
    // Việc hằng ngày ngay dưới Bảng tin, mục rời theo đúng thứ tự (`DAILY`).
    const daily = DAILY.filter((item) => isVisible(item, user.permissions)).map((item) => ({
      key: item.path,
      icon: <IconTile icon={item.icon} hue={hueOfItem(item)} size={24} />,
      label: <Link to={item.path}>{item.label}</Link>,
    }));
    return [
      {
        key: HOME.path,
        icon: <IconTile icon={HOME.icon} hue={hueForPath(HOME.path)} size={24} />,
        label: <Link to={HOME.path}>{HOME.label}</Link>,
      },
      ...daily,
      ...groups,
    ];
  }, [user.permissions, unfinishedToday, accent]);

  const current = findNavigationItem(location.pathname);
  const title = pageTitle(location.pathname);
  // Mở nhiều thẻ cùng lúc (nhập liệu bên này, đối chiếu bên kia) thì phải phân biệt được thẻ nào là gì.
  useEffect(() => {
    document.title = title === APP_NAME ? title : `${title} · ${APP_NAME}`;
    // Đăng xuất (khung ứng dụng bị tháo) thì màn đăng nhập không được mang tên màn hình cũ.
    return () => {
      document.title = APP_NAME;
    };
  }, [title]);
  // Nhóm chứa mục đang xem luôn được mở: vào thẳng bằng đường dẫn (hoặc F5) mà nhóm đó đang gập
  // thì người dùng không thấy mình đang đứng ở đâu trong menu.
  const activeGroup = NAVIGATION.find((group) =>
    group.items.some((item) => item.path === current?.path),
  )?.key;
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((open) => (open.includes(activeGroup) ? open : [...open, activeGroup]));
    }
  }, [activeGroup]);
  // Chọn xong một mục trong ngăn kéo là ngăn kéo đóng lại.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Thanh trên cùng có thể cao lên (rớt dòng ở bề rộng lưng chừng). Phần tử dính bên dưới đọc
  // chiều cao THẬT qua biến CSS thay vì đoán một con số rồi bị nó che mất.
  useLayoutEffect(() => {
    const node = header.current;
    if (!node) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(HEADER_HEIGHT_VAR, `${node.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty(HEADER_HEIGHT_VAR);
    };
  }, []);

  const shortRole = isRoleCode(user.role) ? ROLES[user.role].shortLabel : '?';
  const railCollapsed = collapsed && !isPhone;

  const userMenu: MenuProps['items'] = [
    // Trên điện thoại chỉ còn avatar nên tên người đăng nhập hiện ở đầu menu này.
    ...(isPhone
      ? [
          {
            key: 'who',
            disabled: true,
            label: `${user.displayName} · ${user.roleLabel}`,
          },
          { type: 'divider' as const },
        ]
      : []),
    {
      key: 'account',
      icon: <UserOutlined />,
      label: 'Tài khoản & mật khẩu',
      onClick: () => navigate('/account'),
    },
    // PWA (03/10/2026): cài lên màn hình chính khi máy cài được; đang chạy như ứng dụng (không có nút
    // tải lại của trình duyệt) thì có sẵn «Tải lại ứng dụng».
    // CHUÔNG NHẮC (03/10/2026): bật / tắt thông báo đẩy trên CHÍNH máy này.
    ...(pushAction === 'enable'
      ? [
          {
            key: 'push-on',
            icon: <BellOutlined />,
            label: 'Bật thông báo',
            onClick: () => void enablePush(),
          },
        ]
      : []),
    ...(pushAction === 'disable'
      ? [
          {
            key: 'push-off',
            icon: <BellOffOutlined />,
            label: 'Tắt thông báo',
            onClick: () =>
              void disablePush().then(() => void message.info('Đã tắt thông báo trên máy này')),
          },
        ]
      : []),
    ...(pushAction === 'blocked'
      ? [
          {
            key: 'push-blocked',
            icon: <BellOffOutlined />,
            label: 'Thông báo đang bị chặn',
            onClick: () =>
              void message.warning(
                'Trình duyệt đang chặn thông báo của trang này — mở cài đặt trang (biểu tượng ổ khoá cạnh địa chỉ) rồi cho phép Thông báo',
                8,
              ),
          },
        ]
      : []),
    ...(pwaAction === 'install'
      ? [
          {
            key: 'install',
            icon: <MobileOutlined />,
            label: 'Cài ứng dụng',
            onClick: () => void requestInstall(),
          },
        ]
      : []),
    ...(pwaAction === 'reload'
      ? [
          {
            key: 'reload',
            icon: <ReloadOutlined />,
            label: 'Tải lại ứng dụng',
            onClick: () => window.location.reload(),
          },
        ]
      : []),
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: () => void logout() },
  ];

  const navigationMenu = (
    <Menu
      mode="inline"
      // LUÔN là menu kiểu «light» — tên gọi của Ant Design cho kiểu menu ăn theo token (nền thẻ, chữ
      // thường, mục đang chọn = chữ màu chính trên nền nhạt), nên tự đúng ở cả hai chế độ sáng/tối. Kiểu
      // «dark» của nó là một bảng màu riêng (nền xanh navy, mục chọn tô đặc) không thuộc bảng màu của app.
      theme="light"
      items={menuItems}
      selectedKeys={current ? [current.path] : []}
      openKeys={railCollapsed ? undefined : openGroups}
      onOpenChange={(keys) => {
        // Lúc thanh bên thu gọn, Ant Design báo «không còn nhóm nào mở». Lưu giá trị đó lại
        // là lần sau mở rộng thanh bên mọi nhóm đều gập sạch — nên bỏ qua trong lúc thu gọn.
        if (railCollapsed) return;
        setOpenGroups(keys);
        try {
          localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(keys));
        } catch {
          /* không nhớ được cũng không sao */
        }
      }}
      style={{ borderInlineEnd: 'none' }}
    />
  );

  return (
    // `dvh` chứ không phải `vh`: trên điện thoại `100vh` tính cả phần bị thanh địa chỉ che.
    <Layout style={{ minHeight: '100dvh' }}>
      {isPhone ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          size={288}
          title={<Brand />}
          // Đáy ngăn kéo chừa vạch Home của iPhone khi chạy toàn màn hình.
          styles={{ body: { padding: 0, paddingBottom: 'env(safe-area-inset-bottom)' } }}
        >
          {navigationMenu}
        </Drawer>
      ) : (
        <Sider
          width={248}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          theme="light"
          style={{
            borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: collapsed ? '16px 22px' : '16px 20px' }}>
            {collapsed ? <BrandMark /> : <Brand />}
          </div>
          {navigationMenu}
        </Sider>
      )}
      {/* `minWidth: 0`: không có nó thì một bảng rộng bên trong đẩy cả cột nội dung tràn khỏi màn hình. */}
      <Layout style={{ minWidth: 0 }}>
        <Header
          ref={header}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            height: 'auto',
            minHeight: isPhone ? 56 : 64,
            lineHeight: 'normal',
            paddingBlock: isPhone ? 8 : 12,
            paddingInline: isPhone ? 12 : 20,
          }}
        >
          <Flex align="center" justify="space-between" gap={8} wrap={!isCompact}>
            <Flex align="center" gap={8} style={{ minWidth: 0 }}>
              {isPhone && (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  aria-label="Mở menu"
                  onClick={() => setDrawerOpen(true)}
                />
              )}
              {/* Ô icon màu theo khu vực đứng trước tên màn hình — cùng icon và tông với mục menu. */}
              <IconTile
                icon={current?.icon ?? HOME.icon}
                hue={hueForPath(location.pathname)}
                size={isPhone ? 28 : 32}
              />
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  // Điện thoại: tiêu đề xuống một bậc của thang chữ (16px) cho vừa một hàng với các nút.
                  fontSize: isPhone ? token.fontSizeLG : undefined,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography.Title>
            </Flex>
            {/* Co lại được (`minWidth: 0`): thiếu chỗ thì TÊN người dùng bị cắt «…», không phải cả
                thanh trên cùng tràn khỏi màn hình. */}
            <Flex align="center" gap={isPhone ? 0 : 8} style={{ minWidth: 0 }}>
              {!isCompact && <ViewFilterBar />}
              {isCompact && user.permissions.seesOthers && (
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  title="Xem theo phòng · nhóm · người"
                  content={<ViewFilterBar stacked />}
                >
                  <Badge dot={filtering} offset={[-6, 6]}>
                    <Button
                      type="text"
                      aria-label={
                        filtering
                          ? 'Đang lọc — bấm để đổi bộ lọc xem'
                          : 'Lọc xem theo phòng, nhóm, người'
                      }
                      icon={
                        <FilterOutlined
                          style={{ color: filtering ? token.colorPrimary : undefined }}
                        />
                      }
                    />
                  </Badge>
                </Popover>
              )}
              <Tooltip title="Đổi giao diện sáng / tối">
                <Button
                  type="text"
                  aria-label="Đổi giao diện sáng tối"
                  icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
                  onClick={toggle}
                />
              </Tooltip>
              <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
                <Button
                  type="text"
                  aria-label={`Tài khoản: ${user.displayName}`}
                  style={{
                    height: 40,
                    paddingInline: isPhone ? 4 : 8,
                    minWidth: 0,
                    flex: '0 1 auto',
                  }}
                >
                  <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                    <Avatar
                      size="small"
                      style={{
                        background: token.colorPrimary,
                        fontSize: token.fontSizeSM,
                        flex: 'none',
                      }}
                    >
                      {shortRole}
                    </Avatar>
                    {!isPhone && (
                      <span
                        style={{
                          maxWidth: 210,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.displayName} · {user.roleLabel}
                      </span>
                    )}
                  </Flex>
                </Button>
              </Dropdown>
            </Flex>
          </Flex>
        </Header>
        <Content
          style={{
            padding: isPhone ? 12 : 20,
            // Chạy toàn màn hình trên iPhone: dòng cuối không nằm dưới vạch Home.
            paddingBottom: `calc(${isPhone ? 12 : 20}px + env(safe-area-inset-bottom))`,
          }}
        >
          <InstallApp />
          <PushNotifications />
          <DueTaskNotifier />
          {/* Mã của từng màn hình tải theo nhu cầu (xem `app/router.tsx`) — khung chờ trong lúc tải. */}
          <Suspense fallback={<Skeleton active paragraph={{ rows: 6 }} />}>
            <Outlet />
          </Suspense>
        </Content>
        {/* Ngoài mọi màn hình: đổi mục thì nhạc đang nghe, lô nhạc và video đang đưa lên vẫn chạy tiếp. */}
        <MusicDock />
        <VideoUploadDock />
      </Layout>
    </Layout>
  );
}
